'use client';

import { useEffect, useRef, useState } from 'react';
import { Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useDebouncedSearchParam } from '@kit/ui/hooks/use-debounced-search-param';
import { Input } from '@kit/ui/input';
import { cn } from '@kit/ui/utils';

const SEARCH_DEBOUNCE_MS = 300;

/**
 * The marketplace sub-header (`marketplace-header.tsx`) is `sticky top-0`
 * and `h-14` (3.5rem); this bar sticks directly below it. Keep in sync if
 * the sub-header height changes — the sticky sidebar offset in
 * `../page.tsx` derives from the same stack.
 */
const SUB_HEADER_PX = 56;

/**
 * Marketplace search box. Extracted from the old combined filter bar when the
 * facets moved into the left sidebar (`listing-filters.tsx`). Search is the one
 * global control that sits above the sidebar+results split, full width, so it
 * spans the whole browse surface. Writes `q` (debounced) and resets the keyset
 * cursor via the shared hook.
 *
 * Sticky + condensed on scroll: the wrapper is `sticky top-14` (below the
 * marketplace sub-header). A zero-height sentinel above it feeds an
 * IntersectionObserver — once the sentinel scrolls past the sub-header the
 * bar is "stuck" and condenses (shorter input, narrower width, blurred
 * backdrop) so it reads as chrome rather than page content. Sticky, not
 * fixed, so the body stays the only page scroller (Critical Rule #4).
 */
export function ListingSearch() {
  const { t } = useTranslation();
  const search = useDebouncedSearchParam('q', {
    debounceMs: SEARCH_DEBOUNCE_MS,
    resetParams: ['cursor'],
  });

  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const [stuck, setStuck] = useState(false);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    // jsdom (unit tests) has no IntersectionObserver; the bar then simply
    // never condenses, which is fine — stickiness itself is pure CSS.
    if (!sentinel || typeof IntersectionObserver === 'undefined') return;

    const observer = new IntersectionObserver(
      ([entry]) => setStuck(!(entry?.isIntersecting ?? true)),
      { rootMargin: `-${SUB_HEADER_PX}px 0px 0px 0px` },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);

  return (
    <>
      <div ref={sentinelRef} aria-hidden />
      {/* Stuck = the bar scopes itself to the facet-sidebar rail: it shrinks
          to the sidebar column width (15rem, mirrors the grid template in
          ../page.tsx) and floats above it, so search + facets read as one
          sticky left rail while results scroll freely on the right. The
          full-width wrapper goes pointer-events-none so the strip next to
          the pill never blocks clicks on cards scrolling beneath it. */}
      <div
        className={cn(
          'sticky top-14 z-20 transition-[padding] duration-200',
          stuck && 'pointer-events-none py-2',
        )}
      >
        <div
          className={cn(
            'pointer-events-auto relative transition-all duration-200',
            stuck ? 'max-w-md lg:max-w-[15rem]' : 'max-w-full',
          )}
        >
          {/* z-10: the stuck input's backdrop-blur creates a stacking
              context that would otherwise paint over this icon. */}
          <Search
            className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 z-10 size-4 -translate-y-1/2"
            aria-hidden
          />
          <Input
            id="q"
            name="q"
            type="search"
            value={search.value}
            onChange={(e) => search.setValue(e.target.value)}
            placeholder={t('explore:marketplace.searchPlaceholder')}
            aria-label={t('explore:marketplace.searchAriaLabel')}
            className={cn(
              'pl-9 transition-all duration-200',
              stuck &&
                'bg-background/95 supports-[backdrop-filter]:bg-background/80 h-8 shadow-sm backdrop-blur',
            )}
          />
        </div>
      </div>
    </>
  );
}
